// What one unit cost to put in place — per PRODUCT, never per job.
//
// The formula this replaces divided the WHOLE job's cost by whichever
// production unit had the largest quantity:
//
//     totalSpent / mainQty
//
// which is wrong by construction on any job carrying more than one product.
// 26TM001 Langley Park read $33.94 per block because $413,209.18 — veneer,
// scaffold, cleanup and office overhead included — was divided by the 12,175
// blocks alone. The minor products got no price at all, only a footnote.
//
// The rule here instead: a product is priced ONLY by the money already filed
// to its own task(s).
//
//     price(T) = ( Σ taskTotals[line] for every contract line whose product is T )
//                x (1 + overheadPct)
//                / installedByType[T]
//
// Three things make that honest rather than merely different:
//
//   NUMERATOR — taskTotals is the SAME roll-up the By-Task section prints
//   (js/core/task-costs.js rollUpTasks), so the two views can never disagree
//   about what a task cost. Nothing is recomputed here.
//
//   OVERHEAD — included, because the number exists to bid the next job with
//   and a bid has to carry the office. It is exact, not an allocation guess:
//   totalSpent IS directSpent x (1 + overheadPct), so applying the same
//   fraction to one task's direct cost is that task's arithmetic share.
//
//   DENOMINATOR — the raw installed quantity, everything physically placed.
//   Deliberately NOT allocateInstalled's byLineId, which caps a base line at
//   its estimated_qty and pushes the overflow to a continuation: quantity
//   beyond the estimate still cost money to install, so capping the
//   denominator would overstate the price of a job that ran long.
//
// Whatever cannot carry a per-unit price — cost filed to no task, cost on a
// percent-of-contract line with no product, cost on a product nobody reported
// producing — is returned as `unaccounted`, defined as the REMAINDER. Same
// discipline as rollUpTasks' General: computing it separately is how two
// numbers drift, while a remainder can only ever be right. Every dollar of
// the job is therefore either inside a price or named on that one line.

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

/**
 * @param {object} opts
 * @param {Array}  opts.contractLines   project_contract_lines rows
 * @param {object} opts.typeByLineId    line id → material_type_id (js/core/job-value.js
 *                                      allocateInstalled — it resolves a continuation
 *                                      line's product by walking continues_line_id)
 * @param {object} opts.taskTotals      line id → direct cost filed to that task
 * @param {object} opts.installedByType material_type_id → quantity installed
 * @param {object} opts.typeById        material_type_id → {name, unit}
 * @param {number} opts.totalSpent      direct + overhead, the whole job
 * @param {number} opts.overheadPct     as a FRACTION (0.1 for 10%), never 10
 * @param {object} [opts.earnedByLineId] line id → what that line has EARNED
 *                                      (js/core/job-value.js projectValue's
 *                                      perLine, the same resolution the
 *                                      Contract tab's own totals came from).
 *                                      Optional: without it `earn` is null on
 *                                      every row and the caller shows a price
 *                                      with nothing to compare it against.
 *
 * @returns {{mode: string, rows: Array, unaccounted: number}}
 *   mode  'per_task'  the normal path — every price came from its own task
 *         'whole_job' no contract lines and exactly ONE product, so the whole
 *                     job over that quantity is unambiguous and still true
 *         'unpriced'  nothing can be priced honestly (no contract lines and
 *                     two or more products, or no production at all)
 *   rows  [{typeId, name, unit, qty, estimatedQty, price, earn}] by qty
 *         descending. `price: null` means "nothing filed here yet" — NEVER 0,
 *         which reads off a screen as "this product was free"; `earn: null`
 *         means the same thing on the revenue side (no line of this product
 *         has earned anything yet, or no earnings were passed in).
 *
 *   `earn` is deliberately the MIRROR of `price`: earnings summed over the
 *   same lines, divided by the same raw installed quantity. Only that makes
 *   earn − price an honest margin per unit; deriving it from the sell RATE
 *   instead would compare a rate against a price built from capped, allocated
 *   money and quietly disagree with the Contract tab on any job that ran past
 *   its estimate.
 */
export function unitCostRows(opts) {
  const o = opts || {};
  const lines      = o.contractLines || [];
  const typeByLine = o.typeByLineId || {};
  const taskTotals = o.taskTotals || {};
  const installed  = o.installedByType || {};
  const typeById   = o.typeById || {};
  const totalSpent = num(o.totalSpent);
  const ohMult     = 1 + num(o.overheadPct);

  // The products actually placed on this job. A zero (or missing) quantity is
  // no denominator at all — dividing by it yields Infinity, which would print
  // as a number and be believed.
  const typeIds = Object.keys(installed)
    .filter(id => num(installed[id]) > 0)
    .sort((a, b) => num(installed[b]) - num(installed[a]));

  // Money filed per product, and the contract's own estimate for it. Both are
  // sums over lines, which is what collapses a base line and the change order
  // continuing it into ONE row instead of two competing prices for one
  // product. Only a line carrying its own estimated_qty adds to the estimate:
  // a continuation's cap is a dollar amount, not a quantity.
  const directByType = {};
  const estByType = {};
  const earnedByType = {};
  const earnedByLine = o.earnedByLineId || null;
  lines.forEach(l => {
    if (!l || l.id == null) return;
    const t = typeByLine[l.id];
    if (t == null) return;
    directByType[t] = (directByType[t] || 0) + num(taskTotals[l.id]);
    if (l.material_type_id != null && l.estimated_qty != null) {
      estByType[t] = (estByType[t] || 0) + num(l.estimated_qty);
    }
    // Only a line that HAS an earnings figure contributes one. A line with no
    // sell rate is absent from the map, and adding a 0 for it would drag the
    // product's earn-per-unit down as if the work were being given away.
    if (earnedByLine && earnedByLine[l.id] != null) {
      earnedByType[t] = (earnedByType[t] || 0) + num(earnedByLine[l.id]);
    }
  });

  const row = (id, price) => {
    const t = typeById[id] || {};
    const qty = num(installed[id]);
    const earned = earnedByType[id];
    return {
      typeId: id,
      name: t.name || null,
      unit: t.unit || null,
      qty,
      estimatedQty: estByType[id] == null ? null : estByType[id],
      price,
      earn: earned == null || qty <= 0 ? null : earned / qty,
    };
  };

  // No contract line names any product: there is no task to file cost against,
  // so there is no per-product money to divide.
  if (!Object.keys(directByType).length) {
    if (typeIds.length === 1) {
      // One product IS the job. Dividing the whole job by it is not a
      // simplification, it is the right answer — so the simple job this
      // section was always correct for keeps its number.
      const id = typeIds[0];
      return { mode: 'whole_job', rows: [row(id, totalSpent / num(installed[id]))], unaccounted: 0 };
    }
    return { mode: 'unpriced', rows: typeIds.map(id => row(id, null)), unaccounted: totalSpent };
  }

  let priced = 0;
  const rows = typeIds.map(id => {
    const direct = directByType[id];
    if (!direct) return row(id, null);        // nothing filed — an em dash, not $0.00
    const withOverhead = direct * ohMult;
    priced += withOverhead;
    return row(id, withOverhead / num(installed[id]));
  });

  return { mode: 'per_task', rows, unaccounted: totalSpent - priced };
}
